/* WhatsApp chat widget — Beyond Gifts */
(() => {
  if (customElements.get('whatsapp-chat')) return;
  const KEY = 'wac-pop-closed';
  const store = {
    get: () => { try { return sessionStorage.getItem(KEY); } catch (e) { return null; } },
    set: () => { try { sessionStorage.setItem(KEY, '1'); } catch (e) {} },
  };
  // fixed bars docked at the bottom that the widget should sit above
  const AVOID = ['.toolbar', '.sticky-addcart', '[class*="sticky-add"]', '[data-sticky-bar]'];
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);

  class WhatsappChat extends HTMLElement {
    connectedCallback() {
      this.pop = this.querySelector('.wac-pop');
      this.btn = this.querySelector('.wac-btn');
      this.badge = this.querySelector('.wac-btn__badge');
      this.querySelectorAll('[data-wac-open]').forEach((a) => {
        a.href = this.link();
        a.target = '_blank';
        a.rel = 'noopener';
        a.addEventListener('click', () => this.hidePop(true));
      });
      const close = this.querySelector('[data-wac-close]');
      if (close) close.addEventListener('click', () => this.hidePop(true));

      const text = this.querySelector('[data-wac-text]');
      if (text) text.innerHTML = this.fill(text.innerHTML);

      // any interaction with the pop message keeps it open (cancels auto-close)
      if (this.pop) {
        ['pointerenter', 'pointerdown', 'focusin', 'touchstart'].forEach((ev) =>
          this.pop.addEventListener(ev, () => this.cancelAutoClose(), { passive: true })
        );
      }

      this.schedulePop();

      // Theme editor: selecting this section pins the pop message open for editing
      if (designMode()) {
        const sectionId = (this.closest('.shopify-section') || {}).id || '';
        this.onSelect = (e) => {
          if (!e.detail || `shopify-section-${e.detail.sectionId}` !== sectionId) return;
          clearTimeout(this.timer);
          this.pinned = true;
          this.showPop();
        };
        this.onDeselect = (e) => {
          if (!e.detail || `shopify-section-${e.detail.sectionId}` !== sectionId) return;
          this.pinned = false;
          this.hidePop(false);
          this.schedulePop();
        };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
      }

      document.documentElement.classList.add('wac-on');
      this.onMove = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          this.lift();
        });
      };
      window.addEventListener('scroll', this.onMove, { passive: true });
      window.addEventListener('resize', this.onMove);
      this.onMove();
      setTimeout(this.onMove, 400);
      setTimeout(this.onMove, 1500);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.cancelAutoClose();
      if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
      if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.documentElement.classList.remove('wac-on');
      window.removeEventListener('scroll', this.onMove);
      window.removeEventListener('resize', this.onMove);
    }

    /* "Show pop message after" — in the theme editor the once-per-visit memory is ignored */
    schedulePop() {
      clearTimeout(this.timer);
      if (!this.pop || this.dataset.popup !== 'true') return;
      if (!designMode() && this.dataset.once === 'true' && store.get()) return;
      if (this.badge) this.badge.hidden = false;
      const delay = Math.max(0, Number(this.dataset.delay || 0)) * 1000;
      this.timer = setTimeout(() => this.showPop(), delay);
    }

    fill(str) {
      const product = this.dataset.product || '';
      return String(str || '')
        .replace(/\[product\]/gi, product || 'this gift')
        .replace(/\[url\]/gi, window.location.origin + window.location.pathname)
        .replace(/\[page\]/gi, document.title);
    }

    link() {
      const msg = this.fill(this.dataset.message || '');
      return `https://wa.me/${this.dataset.phone}${msg ? `?text=${encodeURIComponent(msg)}` : ''}`;
    }

    showPop() {
      if (!this.pop) return;
      this.pop.hidden = false;
      requestAnimationFrame(() => this.pop.classList.add('is-open'));
      this.cancelAutoClose();
      // "Auto-close if no interaction after"
      const secs = Number(this.dataset.autoclose || 0);
      if (secs > 0 && !this.pinned) {
        this.autoTimer = setTimeout(() => this.autoClose(), secs * 1000);
      }
    }

    cancelAutoClose() {
      clearTimeout(this.autoTimer);
      this.autoTimer = 0;
    }

    /* closed by itself: fade out, keep the red badge as a reminder */
    autoClose() {
      if (!this.pop || this.pop.hidden || this.pinned) return;
      this.pop.classList.remove('is-open');
      setTimeout(() => {
        if (!this.pop.classList.contains('is-open')) this.pop.hidden = true;
      }, 260);
      if (this.dataset.once === 'true') store.set();
    }

    hidePop(remember) {
      if (!this.pop) return;
      this.cancelAutoClose();
      this.pop.classList.remove('is-open');
      this.pop.hidden = true;
      if (remember && this.badge) this.badge.hidden = true;
      if (remember) store.set();
    }

    /* sit above bottom bars (mobile toolbar, sticky add-to-cart) and push scroll-to-top above the button */
    lift() {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      let extra = 0;
      if (this.dataset.avoid === 'true') {
        AVOID.forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => {
            if (this.contains(el)) return;
            const cs = getComputedStyle(el);
            if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
            if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return;
            const r = el.getBoundingClientRect();
            if (!r.width || !r.height) return;
            if (r.bottom < vh - 40 || r.top > vh) return;
            if (r.right < vw * 0.6) return;
            extra = Math.max(extra, vh - r.top + 8);
          });
        });
      }
      this.style.setProperty('--wac-lift', `${Math.round(extra)}px`);

      const cs = getComputedStyle(this);
      const base = parseFloat(cs.getPropertyValue(vw < 750 ? '--wac-bottom-m' : '--wac-bottom-d')) || 0;
      const size = this.btn ? this.btn.offsetHeight : 0;
      const hidden = cs.display === 'none';
      document.documentElement.style.setProperty('--wac-stack', hidden ? '' : `${Math.round(base + extra + size + 12)}px`);
    }
  }

  customElements.define('whatsapp-chat', WhatsappChat);
})();